import { useMutation, useQuery } from "react-query";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import { loginState } from "../stores/loginState";
import {
  HandexInstance,
  HanjinInstance,
  KpcInstance,
  LogenInstance,
  LotteInstance,
} from "./instance";
import axios from "axios";
import { getKoreaDate, isKpcCompany } from "../utils/kpc";
import {
  clearKpcAutoLoginCredentials,
  saveKpcAutoLoginCredentials,
} from "./kpcAuth";


export const useGetLogin = (userId, userPassword, isAutoLogin = false) => {
  // NOTE 상태 변수 선언
  const [login, setLogin] = useRecoilState(loginState);
  const navigate = useNavigate();

  // NOTE 로컬 아이피 찾는 함수
  const useLocalIP = () => {

    useQuery("localIP", async () => {
      const { data } = await axios.get("https://ipapi.co/json/");
      
      let IP = data.ip;
      if (IP.length > 20) IP = IP.slice(0, 20);

      // IP 조회가 늦게 끝나도 만료 처리로 변경된 최신 로그인 상태를 덮어쓰지 않는다.
      setLogin((prevLogin) => ({
        ...prevLogin,
        localIP: IP,
      }));
    });
  };
  useLocalIP();

  // NOTE 로그인 정보 저장
  const handleLoginSuccess = (data) => {
    const user = data.data[0];

    // KPC에서 자동로그인을 선택한 경우에만 회전형 로그인 키를 보관한다.
    if (isKpcCompany(login.company)) {
      if (isAutoLogin) {
        saveKpcAutoLoginCredentials({ ...user, isAdmin: false });
      } else {
        clearKpcAutoLoginCredentials();
      }
    }

    let isNvr = "0";
    if(user.company === "logen" || login.company === "Logen"){
      isNvr = user.nvr_usable;
    }
    
    setLogin((prevLogin) => ({
      ...prevLogin,
      isLogin: true,
      userId: user.user_id,
      // KPC 로그인 응답에는 지점명(bran_exp)이 없어 점소코드를 대체 표기로 사용한다.
      branchName: user.bran_exp || user.bran_cd,
      branchCode: user.bran_cd,
      saId: user.sa_id || "",
      accountId: user.account_id || "",
      camUsable: user.cam_usable || "",
      camStatus: user.cam_status || "",
      nvrUsable: isNvr,
      // KPC만 당일 로그인 정책을 적용하고 다른 택배사의 기존 로그인 동작은 유지한다.
      kpcLoginDate: isKpcCompany(prevLogin.company) ? getKoreaDate() : "",
    }));

    navigate("/");
  };

  // NOTE 로그인 정보 저장(관리자)
  const handleAdminLoginSuccess = (data) => {
    const user = data.data[0];

    // 관리자 호환 로그인도 일반 KPC 로그인과 동일한 자동로그인 정책을 사용한다.
    if (isKpcCompany(login.company)) {
      if (isAutoLogin) {
        saveKpcAutoLoginCredentials({ ...user, isAdmin: true });
      } else {
        clearKpcAutoLoginCredentials();
      }
    }

    let isNvr = "0";
    if(user.company === "logen" || login.company === "Logen"){
      isNvr = user.nvr_usable;
    }

    setLogin((prevLogin) => ({
      ...prevLogin,
      isLogin: true,
      userId: user.user_id,
      branchName: user.bran_exp || user.bran_cd,
      branchCode: user.bran_cd,
      saId: user.sa_id || "",
      accountId: user.account_id || "",
      camUsable: user.cam_usable || "",
      nvrUsable: isNvr || "0",
      isAdmin: true,
      camStatus: user.cam_status || "",
      // 관리자 호환 로그인(result 77)도 일반 KPC 로그인과 동일한 날짜 정책을 적용한다.
      kpcLoginDate: isKpcCompany(prevLogin.company) ? getKoreaDate() : "",
    }));

    navigate("/");
  };

  // NOTE 로그인 실패 시 (통신 오류)
  const handleLoginError = (error) => {
    console.log(error);
    Swal.fire({
      title: "Error!",
      icon: "error",
      text: "로그인 요청 중 오류가 발생했습니다. 다시 시도해주세요",
      confirmButtonText: "확인",
    });
  };

  // NOTE 로그인 실패 시 (로그인 정보 오류)
  const handleLoginFailure = (result) => {
    if (result === "08") { // 비밀번호 오류
      Swal.fire({
        icon: "warning",
        title: "비밀번호를 확인해주세요",
        confirmButtonText: "확인",
      });
    } else if (result === "25" || result === "09") { // 아이디 오류
      Swal.fire({
        icon: "warning",
        title: "아이디를 확인해주세요",
        confirmButtonText: "확인",
      });
    } else if (result === "fail") { // 아이디 오류
      Swal.fire({
        icon: "error",
        title: "아이디가 존재하지 않습니다!",
        confirmButtonText: "확인",
      });
    }
  };

  // TODO API 결과
  const mutationOptions = {
    // 성공 시
    onSuccess: (data) => {
      if (data.result === "00") {
        handleLoginSuccess(data);
      } else if (data.result === "77") { // 관리자 로그인 시
        handleAdminLoginSuccess(data);
      } else {
        handleLoginFailure(data.result);
      }
    },
    onError: handleLoginError,
  };

  // NOTE 회사 별 인스턴스
  const companyInstances = {
    LOGEN: LogenInstance,
    LOTTE: LotteInstance,
    HANJIN: HanjinInstance,
    HANDEX: HandexInstance,
    KPC: KpcInstance,
  };

  const selectedInstance = companyInstances[login.company];

  // TODO API 리퀘스트 정보
  const loginMutation = useMutation(() => {
    if (selectedInstance) {
      const loginData = {
        api: "signIn",
        data: [{ user_id: userId, user_password: userPassword }],
      };

      // 기존 택배사 요청 형식은 유지하고 KPC에만 문서상 요청 형식을 적용한다.
      if (login.company !== "KPC") {
        loginData.data[0].company = login.company.toLowerCase();
      }

      return selectedInstance.post("/watcher/sign", loginData);
    } else {
      throw new Error("Invalid company");
    }
  }, mutationOptions);

  return loginMutation;
};
